import axios from 'axios';

interface WordPressPlugin {
  plugin: string;
  status: 'active' | 'inactive';
  name: string;
  plugin_uri?: string;
  author?: string;
  author_uri?: string;
  description?: string;
  version: string;
  network_only?: boolean;
  requires_wp?: string;
  requires_php?: string;
  textdomain?: string;
}

/**
 * Fetches the list of plugins from a WordPress site
 * @param siteUrl The base URL of the WordPress site (e.g., 'https://example.com')
 * @param username Basic Auth username (if required)
 * @param password Basic Auth password (if required)
 * @returns Promise with the list of plugins
 */
export async function getWordPressPlugins(
  siteUrl: string,
  username?: string,
  password?: string
): Promise<WordPressPlugin[]> {
  try {
    const url = `${siteUrl.replace(/\/$/, '')}/wp-json/wp/v2/plugins`;
    
    const config: any = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    // Add Basic Auth if credentials are provided
    if (username && password) {
      const auth = btoa(`${username}:${password}`);
      config.headers['Authorization'] = `Basic ${auth}`;
    }

    const response = await axios.get(url, config);
    
    if (response.status === 200) {
      // Check if the response is an array
      if (!Array.isArray(response.data)) {
        console.error('Unexpected plugins response format:', response.data);
        throw new Error('The WordPress API returned an unexpected response format. The site might require authentication or have a security plugin blocking the request.');
      }
      return response.data;
    }

    throw new Error(`Failed to fetch plugins. Status: ${response.status}`);
  } catch (error) {
    console.error('Error fetching WordPress plugins:', error);
    
    if (axios.isAxiosError(error)) {
      console.error('Axios error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      });
      
      if (error.response?.status === 401) {
        throw new Error('Authentication failed. Please check your WordPress username and Application Password.');
      } else if (error.response?.status === 403) {
        throw new Error('Access forbidden. The user may not have permission to view plugins or a security plugin is blocking the request.');
      } else if (error.response?.status === 404) {
        throw new Error('The WordPress REST API endpoint was not found. Make sure:' +
          '\n1. The site is a WordPress site' +
          '\n2. The REST API is enabled (some security plugins disable it)' +
          '\n3. The URL is correct and includes https:// if using SSL');
      } else if (error.code === 'ENOTFOUND') {
        throw new Error(`Could not connect to the site. Please check the URL and try again. (${error.message})`);
      }
    }
    
    throw new Error(`Failed to fetch plugins: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
  }
}

/**
 * Gets a single plugin's details
 * @param siteUrl The base URL of the WordPress site
 * @param pluginFile The plugin file (e.g., 'akismet/akismet.php')
 * @param username Basic Auth username (if required)
 * @param password Basic Auth password (if required)
 * @returns Promise with the plugin details
 */
export async function getWordPressPlugin(
  siteUrl: string,
  pluginFile: string,
  username?: string,
  password?: string
): Promise<WordPressPlugin> {
  try {
    const url = `${siteUrl.replace(/\/$/, '')}/wp-json/wp/v2/plugins/${encodeURIComponent(pluginFile)}`;
    
    const config: any = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    // Add Basic Auth if credentials are provided
    if (username && password) {
      const auth = btoa(`${username}:${password}`);
      config.headers['Authorization'] = `Basic ${auth}`;
    }

    const response = await axios.get<WordPressPlugin>(url, config);
    
    if (response.status === 200) {
      return response.data;
    }

    throw new Error(`Failed to fetch plugin. Status: ${response.status}`);
  } catch (error) {
    console.error(`Error fetching WordPress plugin ${pluginFile}:`, error);
    throw error;
  }
}
